// @givmo/board — the Governance page's Officers part: the board's own officer titles (CBG-YR3-ROLES
// layer 2, D-CBG-21; the `l2-officers` and `l2-officers-rename` frames).
//
// One card, the titles in the board's own order: each title's name (with "Givmo’s “Chair”, renamed" or
// "Your board’s own" beneath it), who holds it now, and the Owner's row menu — Rename · Move up · Move
// down · Retire. Retired titles sit under their own heading: a title someone has held is never deleted.
//
// Changing them is the Owner's (`board_member:manage`, the roster's own permission — no new string);
// everyone else reads the list without the menu. Who holds a title is set on each member's page, never
// here, so nothing on this card changes anyone's titles: renaming moves the words everywhere they are
// shown (a signature already recorded keeps the words it was made with), and the backend refuses to
// retire a title while anyone holds it. A default nobody has used yet has no id: the writes name it by
// its key, and the first of them stores every default.
//
// A title is a label (§3.2, §3.4): no line here says what an office may do or what the law asks of it.
//
// CBG-YR3-ROLES layer 3 (`l3-duties-start`, `l3-duties`, `l3-duties-edit`; backend contract
// `board-officer-roles.md` §14) adds a second card, "What each title does in Givmo": Board's own acts
// — presiding, signing the minutes, keeping the minute book, sending notices and the packet, presenting
// the financial report — tied to the board's titles. Givmo's starting point does nothing until the
// Owner uses it ("Use these as our board's duties"); "Change" opens the dialog that picks a title for
// each act and notes where the bylaws say so. Every save is a new adoption and the earlier ones stay
// readable under "Show N earlier versions". The duties decide whom Board asks to sign the minutes and
// what a few dialogs suggest — never who may act, which the card's "How this works" says.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isGivmoError } from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  Dialog,
  ErrorMessage,
  RowActions,
  Select,
  Skeleton,
  TextField,
  toast,
} from '@givmo/design-system';
import { hasBoardPermission, useBoardHost } from '../../board-host';
import { useBoardOperations } from '../../board-runtime';
import { FieldNote } from '../../components/field-note';
import { HowThisWorks, StatusWord } from '../../components/fine-print';
import { displayDate, todayAsDateInput } from '../../lib/display';
import { extractBoardMembers } from '../roster/roster-decode';
import {
  adoptionGroups,
  adoptionRefs,
  draftRefs,
  dutyDrafts,
  dutyGroups,
  dutySource,
  dutyTitleChoices,
  dutyWords,
  extractOfficerDuties,
  NO_TITLE,
  type DutyDraft,
  type OfficerDuties,
} from '../roster/officer-duties';
import {
  catalogRowNote,
  extractOfficerCatalog,
  joinWords,
  titleHolderRows,
  type OfficerCatalogRow,
} from '../roster/officer-roles';
import { officerRolesL2Words as l2 } from '../roster/officer-roles-l2-words';
import { officerRolesL3Words as l3 } from '../roster/officer-roles-l3-words';
import { GovernancePartHeader } from './governance-part-header';

const ROW =
  'flex flex-wrap items-center justify-between gap-12 border-t border-border-subtle py-14 first:border-t-0 first:pt-0 last:pb-0';
const NAME = 'font-sans text-s14 font-w600 tracking-givmo text-text-strong wrap-anywhere';
const QUIET = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary';
const SECONDARY = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary';

type Editing =
  | { kind: 'add' }
  | { kind: 'rename'; row: OfficerCatalogRow }
  | { kind: 'retire'; row: OfficerCatalogRow };

/** The Officers part of the Governance page. */
export function OfficersPart() {
  const operations = useBoardOperations();
  const queryClient = useQueryClient();
  const { permissions, orgRef, isReady } = useBoardHost();
  const canManage = hasBoardPermission(permissions, 'board_member:manage');
  const org = orgRef ?? '';
  const enabled = isReady && orgRef !== null;
  const [editing, setEditing] = useState<Editing | null>(null);

  const catalogOptions = operations.officerListBoardOfficerTitlesOptions({
    path: { org_ref: org },
  });
  const catalogQuery = useQuery({ ...catalogOptions, enabled, select: extractOfficerCatalog });
  const seatsOptions = operations.officerListBoardMembersOptions({ path: { org_ref: org } });
  const seatsQuery = useQuery({ ...seatsOptions, enabled, select: extractBoardMembers });
  // Layer 3: the duties in force — else Givmo's starting point — and the earlier adoptions.
  const dutiesOptions = operations.officerGetBoardOfficerDutiesOptions({ path: { org_ref: org } });
  const dutiesQuery = useQuery({ ...dutiesOptions, enabled, select: extractOfficerDuties });
  const [editingDuties, setEditingDuties] = useState(false);

  const catalog = catalogQuery.data ?? [];
  // The reader's calendar day: a holder whose recorded term end is before it is held over.
  const today = todayAsDateInput();
  const active = catalog.filter((row) => !row.retired);
  const retired = catalog.filter((row) => row.retired);
  const seats = seatsQuery.data ?? [];

  // A rename shows on every page that names the title, so the roster re-reads with the catalog — and
  // so do the duties, which name the titles in their current words (a retirement leaves its duties
  // "not assigned to a title"). Adopting a default by its key stores the defaults, so an adoption
  // re-reads all three too.
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: catalogOptions.queryKey }),
      queryClient.invalidateQueries({ queryKey: seatsOptions.queryKey }),
      queryClient.invalidateQueries({ queryKey: dutiesOptions.queryKey }),
    ]);

  // "Use these as our board's duties": Givmo's starting point, adopted as it stands.
  const adopt = useMutation({
    mutationFn: async (read: OfficerDuties) => {
      await operations.officerAdoptBoardOfficerDuties({
        path: { org_ref: org },
        body: { duties: adoptionRefs(read.duties) },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success(l3.dutiesAdoptedToast);
      void refresh();
    },
    onError: (caught) => {
      toast.error(isGivmoError(caught) ? caught.message : l3.dutiesFailed);
    },
  });

  const move = useMutation({
    mutationFn: async ({ row, position }: { row: OfficerCatalogRow; position: number }) => {
      await operations.officerUpdateBoardOfficerTitle({
        path: { org_ref: org, title_ref: row.ref },
        body: { position },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success(l2.titleMoved);
      void refresh();
    },
    onError: (caught) => {
      toast.error(isGivmoError(caught) ? caught.message : l2.officersFailed);
    },
  });

  return (
    <div className="flex flex-col gap-24" data-testid="governance-officers">
      <GovernancePartHeader
        description={l2.officersLead}
        actions={
          // Adding a title is `board_member:manage`: without it, no button.
          canManage ? (
            <Button
              appearance="saas"
              tone="secondary"
              disabled={!isReady}
              onClick={() => setEditing({ kind: 'add' })}
              testId="officers-add-title"
            >
              {l2.addTitle}
            </Button>
          ) : undefined
        }
      />

      {catalogQuery.isPending && enabled ? (
        <Skeleton className="h-[180px] w-full rounded-lg" testId="officers-loading" />
      ) : null}

      {catalogQuery.isError ? (
        <Card testId="officers-error">
          <CardBody className="flex flex-col items-start gap-12">
            <p className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
              {l2.officersLoadFailed}
            </p>
            <Button
              appearance="saas"
              tone="secondary"
              onClick={() => void catalogQuery.refetch()}
              testId="officers-error-retry"
            >
              Try again
            </Button>
          </CardBody>
        </Card>
      ) : null}

      {catalogQuery.isSuccess ? (
        <Card testId="officers-titles">
          <CardBody className="flex flex-col gap-12">
            <div className="flex flex-wrap items-baseline justify-between gap-8">
              <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
                {l2.titlesCard}
              </h2>
              <HowThisWorks lines={l2.titlesHow} testId="officers-titles-how" align="end" />
            </div>
            <ul className="flex flex-col">
              {active.map((row, index) => {
                const holders = titleHolderRows(row, seats, today);
                const names = holders.map((holder) => holder.name);
                const note = catalogRowNote(row);
                return (
                  <li key={row.ref} className={ROW} data-testid={`officers-title-${row.ref}`}>
                    <div className="flex min-w-0 flex-col gap-2">
                      <span className={NAME}>{row.label}</span>
                      {note !== null ? <span className={QUIET}>{note}</span> : null}
                    </div>
                    <div className="flex min-w-0 items-center gap-12">
                      <div className="flex min-w-0 flex-col gap-2 sm:items-end sm:text-right">
                        <span
                          className={`${names.length > 0 ? SECONDARY : QUIET} wrap-anywhere`}
                          data-testid={`officers-title-holders-${row.ref}`}
                        >
                          {names.length > 0 ? joinWords(names) : l2.nobodyHolds}
                        </span>
                        {/* CBG-YR3-ROLES-HOLDOVER: a holder whose recorded term end has passed still
                            holds the title until an Owner ends it or changes its dates — said here,
                            so the Owner can act. */}
                        {holders
                          .filter((holder) => holder.heldOverSince !== null)
                          .map((holder) => (
                            <span
                              key={holder.name}
                              className={`${QUIET} wrap-anywhere`}
                              data-testid={`officers-title-holdover-${row.ref}`}
                            >
                              {holders.length > 1
                                ? l3.termEndedStillServingFor(
                                    holder.name,
                                    displayDate(holder.heldOverSince),
                                  )
                                : l3.termEndedStillServing(displayDate(holder.heldOverSince))}
                            </span>
                          ))}
                      </div>
                      {canManage ? (
                        <RowActions
                          label={l2.actionsFor(row.label)}
                          disabled={move.isPending}
                          testId={`officers-title-actions-${row.ref}`}
                          actions={[
                            {
                              label: l2.rename,
                              testId: `officers-title-rename-${row.ref}`,
                              onSelect: () => setEditing({ kind: 'rename', row }),
                            },
                            ...(index > 0
                              ? [
                                  {
                                    label: l2.moveUp,
                                    testId: `officers-title-up-${row.ref}`,
                                    onSelect: () => move.mutate({ row, position: index }),
                                  },
                                ]
                              : []),
                            ...(index < active.length - 1
                              ? [
                                  {
                                    label: l2.moveDown,
                                    testId: `officers-title-down-${row.ref}`,
                                    onSelect: () => move.mutate({ row, position: index + 2 }),
                                  },
                                ]
                              : []),
                            {
                              label: l2.retire,
                              testId: `officers-title-retire-${row.ref}`,
                              onSelect: () => setEditing({ kind: 'retire', row }),
                            },
                          ]}
                        />
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
            {retired.length > 0 ? (
              <div
                className="flex flex-col gap-8 border-t border-border-subtle pt-12"
                data-testid="officers-retired"
              >
                <span className="font-sans text-s12 font-w600 uppercase tracking-wide text-text-tertiary">
                  {l2.retired}
                </span>
                {retired.map((row) => (
                  <span key={row.ref} className="flex flex-wrap items-baseline gap-x-8">
                    <span className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary wrap-anywhere">
                      {row.label}
                    </span>
                    {row.retired_at !== null ? (
                      <span className={QUIET}>{l2.retiredOn(displayDate(row.retired_at))}</span>
                    ) : null}
                  </span>
                ))}
              </div>
            ) : null}
          </CardBody>
        </Card>
      ) : null}

      {/* Layer 3: what each title does in Givmo. It names the catalog's titles, so it waits for both. */}
      {catalogQuery.isSuccess && dutiesQuery.isSuccess ? (
        <DutiesCard
          read={dutiesQuery.data}
          catalog={catalog}
          canManage={canManage}
          adopting={adopt.isPending}
          onAdopt={() => adopt.mutate(dutiesQuery.data)}
          onChange={() => setEditingDuties(true)}
        />
      ) : null}
      {catalogQuery.isSuccess && dutiesQuery.isError ? (
        <Card testId="officers-duties-error">
          <CardBody className="flex flex-col items-start gap-12">
            <p className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
              {l3.dutiesLoadFailed}
            </p>
            <Button
              appearance="saas"
              tone="secondary"
              onClick={() => void dutiesQuery.refetch()}
              testId="officers-duties-error-retry"
            >
              Try again
            </Button>
          </CardBody>
        </Card>
      ) : null}

      {editingDuties && orgRef !== null && dutiesQuery.isSuccess ? (
        <DutiesDialog
          orgRef={orgRef}
          read={dutiesQuery.data}
          catalog={catalog}
          onClose={() => setEditingDuties(false)}
          onDone={() => {
            setEditingDuties(false);
            void refresh();
          }}
        />
      ) : null}

      {editing !== null && orgRef !== null ? (
        <TitleDialog
          orgRef={orgRef}
          editing={editing}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            void refresh();
          }}
        />
      ) : null}
    </div>
  );
}

/** Add a title, rename one, or confirm a retirement: one dialog, three acts. */
function TitleDialog({
  orgRef,
  editing,
  onClose,
  onDone,
}: {
  orgRef: string;
  editing: Editing;
  onClose: () => void;
  onDone: () => void;
}) {
  const operations = useBoardOperations();
  const [label, setLabel] = useState(editing.kind === 'rename' ? editing.row.label : '');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      if (editing.kind === 'add') {
        await operations.officerCreateBoardOfficerTitle({
          path: { org_ref: orgRef },
          body: { label: label.trim() },
          throwOnError: true,
        });
      } else if (editing.kind === 'rename') {
        await operations.officerUpdateBoardOfficerTitle({
          path: { org_ref: orgRef, title_ref: editing.row.ref },
          body: { label: label.trim() },
          throwOnError: true,
        });
      } else {
        await operations.officerRetireBoardOfficerTitle({
          path: { org_ref: orgRef, title_ref: editing.row.ref },
          throwOnError: true,
        });
      }
    },
    onSuccess: () => {
      toast.success(
        editing.kind === 'add'
          ? l2.titleAdded
          : editing.kind === 'rename'
            ? l2.titleRenamed
            : l2.titleRetired,
      );
      onDone();
    },
    onError: (caught) => {
      setError(isGivmoError(caught) ? caught.message : l2.officersFailed);
    },
  });

  const heading =
    editing.kind === 'add'
      ? l2.addTitle
      : editing.kind === 'rename'
        ? l2.renameTitle(editing.row.label)
        : l2.retireTitle(editing.row.label);
  const canSave =
    !save.isPending &&
    (editing.kind === 'retire' ||
      (label.trim().length > 0 &&
        !(editing.kind === 'rename' && label.trim() === editing.row.label)));

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !save.isPending && onClose()}>
      <Dialog.Content
        aria-label={heading}
        className="flex flex-col gap-16"
        testId={`officers-${editing.kind}-dialog`}
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {heading}
        </Dialog.Title>

        {editing.kind === 'retire' ? (
          <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
            {l2.retireBody}
          </p>
        ) : (
          <>
            <TextField
              label={l2.titleField}
              value={label}
              maxLength={80}
              onChange={(event) => setLabel(event.target.value)}
              testId={`officers-${editing.kind}-field`}
            />
            <FieldNote>{editing.kind === 'rename' ? l2.renameNote : l2.addTitleNote}</FieldNote>
          </>
        )}

        {error !== null ? (
          <ErrorMessage testId={`officers-${editing.kind}-error`}>{error}</ErrorMessage>
        ) : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={save.isPending}
            onClick={onClose}
            testId={`officers-${editing.kind}-cancel`}
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone={editing.kind === 'retire' ? 'danger' : 'primary'}
            loading={save.isPending}
            disabled={!canSave}
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            testId={`officers-${editing.kind}-save`}
          >
            {editing.kind === 'add'
              ? l2.addButton
              : editing.kind === 'rename'
                ? l2.renameButton
                : l2.retireButton}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

/** "What each title does in Givmo" (layer 3): the duties by title, then those no title does. */
function DutiesCard({
  read,
  catalog,
  canManage,
  adopting,
  onAdopt,
  onChange,
}: {
  read: OfficerDuties;
  catalog: readonly OfficerCatalogRow[];
  canManage: boolean;
  adopting: boolean;
  onAdopt: () => void;
  onChange: () => void;
}) {
  const [showEarlier, setShowEarlier] = useState(false);
  const adoption = read.adopted;
  const { groups, unassigned } = dutyGroups(read.duties, adoption !== null, catalog);
  const adoptedOn = adoption === null ? null : displayDate(adoption.adopted_at);

  return (
    <Card testId="officers-duties">
      <CardBody className="flex flex-col gap-12">
        <div className="flex flex-wrap items-start justify-between gap-12">
          <div className="flex min-w-0 flex-col gap-4">
            <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
              {l3.dutiesTitle}
            </h2>
            <span className="inline-flex flex-wrap items-center gap-x-8">
              {adoption !== null ? (
                <StatusWord testId="officers-duties-adopted">
                  {adoption.adopted_by_display_name !== null
                    ? l3.dutiesAdopted(adoption.adopted_by_display_name, adoptedOn ?? '')
                    : l3.dutiesAdoptedOn(adoptedOn ?? '')}
                </StatusWord>
              ) : (
                <StatusWord tone="act" testId="officers-duties-start">
                  {l3.dutiesStart}
                </StatusWord>
              )}
              <HowThisWorks lines={l3.dutiesHow} testId="officers-duties-how" />
            </span>
          </div>
          {/* Adopting and changing them is the Owner's (`board_member:manage`): without it, the
              duties read with no buttons. */}
          {canManage ? (
            <div className="flex flex-wrap gap-10">
              <Button
                appearance="saas"
                tone="secondary"
                disabled={adopting}
                onClick={onChange}
                testId="officers-duties-change"
              >
                {l3.change}
              </Button>
              {adoption === null ? (
                <Button
                  appearance="saas"
                  tone="primary"
                  loading={adopting}
                  disabled={adopting}
                  onClick={onAdopt}
                  testId="officers-duties-adopt"
                >
                  {l3.useThese}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>

        <ul className="flex flex-col">
          {groups.map((group) => (
            <li
              key={group.key}
              className="flex flex-col gap-6 border-t border-border-subtle py-12 first:border-t-0 first:pt-0"
              data-testid={`officers-duties-${group.ref}`}
            >
              <span className={NAME}>{group.label}</span>
              {group.lines.map((line) => {
                const source = dutySource(line, adoption !== null);
                return (
                  <div
                    key={line.act_key}
                    className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between sm:gap-16"
                  >
                    <span className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
                      {dutyWords(line.act_key)}
                    </span>
                    {/* A source shared by the title's every duty is said once, under them. */}
                    {group.sharedSource === undefined && source !== null ? (
                      <span className={`${QUIET} wrap-anywhere`}>{source}</span>
                    ) : null}
                  </div>
                );
              })}
              {group.sharedSource ? (
                <span className={`${QUIET} wrap-anywhere`}>{group.sharedSource}</span>
              ) : null}
            </li>
          ))}
          {unassigned.length > 0 ? (
            <li
              className="flex flex-col gap-6 border-t border-border-subtle py-12 first:border-t-0 first:pt-0 last:pb-0"
              data-testid="officers-duties-unassigned"
            >
              <span className="font-sans text-s14 font-w600 tracking-givmo text-text-tertiary">
                {l3.notAssigned}
              </span>
              {unassigned.map((line) => (
                <span
                  key={line.act_key}
                  className="font-sans text-s14 font-w400 tracking-givmo text-text-tertiary"
                >
                  {dutyWords(line.act_key)}
                </span>
              ))}
            </li>
          ) : null}
        </ul>

        {adoption !== null && read.earlier.length > 0 ? (
          <div className="flex flex-col gap-12">
            <button
              type="button"
              aria-expanded={showEarlier}
              onClick={() => setShowEarlier((open) => !open)}
              className="self-start font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline"
              data-testid="officers-duties-history"
            >
              {showEarlier ? l3.hideEarlierVersions : l3.earlierVersions(read.earlier.length)}
            </button>
            {showEarlier
              ? read.earlier.map((earlier) => {
                  const earlierGroups = adoptionGroups(earlier);
                  return (
                    <div
                      key={earlier.id}
                      className="flex flex-col gap-6 border-t border-border-subtle pt-12"
                      data-testid={`officers-duties-earlier-${earlier.id}`}
                    >
                      <span className="font-sans text-s13 font-w600 tracking-givmo text-text-secondary">
                        {l3.earlierVersion(
                          displayDate(earlier.adopted_at),
                          displayDate(earlier.superseded_at),
                        )}
                      </span>
                      {earlierGroups.length === 0 ? (
                        <span className={QUIET}>{l3.earlierNone}</span>
                      ) : (
                        earlierGroups.map((group) => (
                          <span key={group.label} className="flex flex-wrap items-baseline gap-x-8">
                            <span className="font-sans text-s13 font-w600 tracking-givmo text-text-secondary wrap-anywhere">
                              {group.label}
                            </span>
                            <span className={`${QUIET} wrap-anywhere`}>
                              {group.duties.join(' · ')}
                            </span>
                          </span>
                        ))
                      )}
                    </div>
                  );
                })
              : null}
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

/** "Change": a title for each of Board's acts, and where the bylaws say so (`l3-duties-edit`). */
function DutiesDialog({
  orgRef,
  read,
  catalog,
  onClose,
  onDone,
}: {
  orgRef: string;
  read: OfficerDuties;
  catalog: readonly OfficerCatalogRow[];
  onClose: () => void;
  onDone: () => void;
}) {
  const operations = useBoardOperations();
  const choices = dutyTitleChoices(catalog);
  const [drafts, setDrafts] = useState<DutyDraft[]>(() => dutyDrafts(read.duties));
  const [error, setError] = useState<string | null>(null);
  const firstAdoption = read.adopted === null;
  const options = [
    ...choices.map((choice) => ({ value: choice.key, label: choice.label })),
    { value: NO_TITLE, label: l3.noTitle },
  ];
  const setDraft = (act: string, change: Partial<DutyDraft>) =>
    setDrafts((rows) => rows.map((row) => (row.act_key === act ? { ...row, ...change } : row)));

  const save = useMutation({
    mutationFn: async () => {
      await operations.officerAdoptBoardOfficerDuties({
        path: { org_ref: orgRef },
        body: { duties: draftRefs(drafts, choices) },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success(firstAdoption ? l3.dutiesAdoptedToast : l3.dutiesSavedToast);
      onDone();
    },
    onError: (caught) => {
      setError(isGivmoError(caught) ? caught.message : l3.dutiesFailed);
    },
  });

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !save.isPending && onClose()}>
      <Dialog.Content
        aria-label={l3.dutiesEditorTitle}
        className="flex max-h-[80vh] flex-col gap-16 overflow-y-auto"
        testId="officers-duties-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {l3.dutiesEditorTitle}
        </Dialog.Title>
        <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          {l3.dutiesEditorLead}
        </p>
        {drafts.map((draft) => (
          <div
            key={draft.act_key}
            role="group"
            aria-label={dutyWords(draft.act_key)}
            className="flex flex-col gap-10 border-t border-border-subtle pt-14"
            data-testid={`officers-duty-${draft.act_key}`}
          >
            <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
              {dutyWords(draft.act_key)}
            </span>
            <div className="grid grid-cols-1 gap-12 sm:grid-cols-2">
              <Select
                label={l3.dutyTitleField}
                appearance="saas"
                value={draft.title}
                onValueChange={(next) => setDraft(draft.act_key, { title: next ?? NO_TITLE })}
                options={options}
                testId={`officers-duty-title-${draft.act_key}`}
              />
              <TextField
                label={l3.dutyCitationField}
                value={draft.citation}
                maxLength={200}
                onChange={(event) => setDraft(draft.act_key, { citation: event.target.value })}
                testId={`officers-duty-citation-${draft.act_key}`}
              />
            </div>
          </div>
        ))}

        {error !== null ? (
          <ErrorMessage testId="officers-duties-dialog-error">{error}</ErrorMessage>
        ) : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={save.isPending}
            onClick={onClose}
            testId="officers-duties-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={save.isPending}
            disabled={save.isPending}
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            testId="officers-duties-save"
          >
            {l3.save}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
